/**
 * Multi-company app-bar scope helpers (WI-1: global CompanyScopeSwitcher).
 *
 * The global company *scope* is a single per-user filter surfaced in the app
 * bar — distinct from the on-record company (`DocumentCompanyField`). See
 * `docs/platform/multi-company-design.md` §"Global company scope (app bar)".
 *
 * Pure, React-free helpers so both the axios interceptor (non-React) and React
 * components can share the same participation + surface logic.
 */
import { getModuleByPath } from '../config/modules.config';
import { isModuleCompanyScoped } from './companyScopingStore';

/**
 * True when the given module id participates in the multi-company dimension for
 * the current tenant.
 *
 * Participation is per-tenant and dynamic (WI-4): the ERP cluster + people are
 * always on, other opt-in modules are enabled per-module in their admin
 * settings. The live map is held in `companyScopingStore`; before it loads (and
 * for modules it omits) this falls back to the always-on ERP set, so the
 * pre-load behaviour matches the original hardcoded default.
 */
export function isCompanyScopedModuleId(moduleId: string): boolean {
  return isModuleCompanyScoped(moduleId);
}

/** True when the URL's module participates in the global app-bar scope. */
export function isCompanyScopedPath(pathname: string): boolean {
  try {
    return isCompanyScopedModuleId(getModuleByPath(pathname).id);
  } catch {
    return false;
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Verbs that name an EXISTING record even when its id is not in the path (a
 * singleton edit form). `new`/`create` are deliberately NOT here — see
 * `isCollectionSurface`.
 */
const EXISTING_RECORD_VERB_SEGMENTS = new Set(['edit']);

/**
 * Heuristic for "is this a collection surface" (list / dashboard / report /
 * kanban / create form) vs a single-record surface (detail / edit form).
 *
 * The app bar renders above the JSX `<Routes>` tree, so the matched page-type
 * is not available as route metadata — we infer it from the URL. A path is a
 * single-record surface when any segment is a record id (UUID) or the last
 * segment is `edit`. Everything else is treated as a collection surface.
 *
 * **A record-less `/new` (or `/create`) path is a COLLECTION surface**, not a
 * record one. It used to be lumped in with `edit`, which hid the scope control
 * on every create page in a participating module — including the inquiry
 * importer, whose own empty state instructs the user to pick a company from
 * that very control and which cannot proceed until they do. The scope is not
 * "this record's company" there, because there is no record yet: it is what
 * seeds the company the page WILL create (see multi-company design,
 * §"Creating a new record — company precedence", rule 2). A create page nested
 * under a parent record (`/newsletters/{id}/issues/new`) still hides it — the
 * parent already fixes the company, so the UUID rule applies first.
 *
 * PanLuma uses UUID-only primary keys, so we deliberately do NOT treat bare
 * integer segments as record ids — those are literal route names (e.g. the
 * `1099` reporting center, `/accounting/reports/1099`), not detail pages.
 *
 * The scope value is never mutated by *this* predicate — it only governs the
 * control's *visibility*. Nor by navigation on its own: the one declared
 * exception is a link that names a company EXPLICITLY in its query string
 * (`?company=`), which `modules/accounting/hooks/useAlignCompanyScope.ts`
 * honours so a month-end cross-link can be about the period it came from. A
 * path alone still never moves the scope.
 */
export function isCollectionSurface(pathname: string): boolean {
  const segments = pathname.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (segments.length === 0) return true;

  if (segments.some((seg) => UUID_RE.test(seg))) return false;

  const last = segments[segments.length - 1].toLowerCase();
  return !EXISTING_RECORD_VERB_SEGMENTS.has(last);
}

/**
 * Should the global app-bar scope control be shown for this URL?
 * (Participating module AND a collection surface. The ≥2-company gate is
 * applied by the component, which has the companies query.)
 */
export function shouldShowCompanyScopeForPath(pathname: string): boolean {
  return isCompanyScopedPath(pathname) && isCollectionSurface(pathname);
}
